import { useCallback, useMemo } from "react";
import { Icon } from "@iconify/react";
import { useNavigate, useSearchParams } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import ChartCard from "../components/ChartCard";
import DataTable, { Column } from "../components/DataTable";
import StatTile from "../components/StatTile";
import Avatar from "../components/Avatar";
import CountChartCard from "../components/CountChartCard";
import FunnelBars from "../components/charts/FunnelBars";
import { EmptyChartNote } from "../components/charts/StackedColumns";
import { TagChip } from "../components/Pills";
import { CardGrid, ErrorBanner, LoadingNote, NoteCard } from "../components/Layout";
import { useDashboardData } from "../hooks/useDashboardData";
import {
  fetchInstagramAccounts,
  fetchInstagramFunnel,
  fetchInstagramHistory,
} from "../dashboardApi";
import {
  InstagramAccountRow,
  InstagramAccountsResponse,
  InstagramFunnelResponse,
  InstagramHistoryResponse,
} from "../types";
import { dashboardPath } from "../routes";
import { colors, type, viz, vizRamp } from "../styles";
import { formatDate, formatNumber, formatRelative, formatUtcDayKey } from "../format";

/**
 * Everything Markit knows about Instagram, in one place.
 *
 * **This screen used to be the audit view BEHIND a funnel that lived on the Home
 * tab. Since Sep 2026 it is both, and that is the point of the change.** Home's
 * Activation section was entirely Instagram — the funnel chart plus three stat
 * tiles duplicating figures this screen already showed — so a reader had two
 * places to look and two summaries that had to be kept in step. Jonathan's call
 * was that Home should carry no Instagram at all, so the funnel came here, where
 * the per-creator table and the four timeline charts behind its own steps
 * already were.
 *
 * **Only the funnel came across, and that is deliberate.** The three tiles
 * beside it on Home were "accounts connected" (identical to this screen's own
 * tile), "automations created" (this screen's Automations tile, which shows the
 * with-drafts total and the live figure per row) and "automations triggered"
 * (carried on the Never fired tile's second line). Moving them would have put
 * two copies of one number on one screen. What genuinely did not exist here is
 * the FUNNEL SHAPE — connect → create → trigger as one population narrowing —
 * which three separate tiles cannot express, and which is the only thing on this
 * screen that answers "where do creators drop out".
 *
 * **Three views of the same feature, and they are not redundant:**
 *  - the funnel — how far the population gets, right now;
 *  - the four charts — how each step has moved over time;
 *  - the table — who, specifically, and what their setup looks like.
 *
 * **Two live endpoints, fetched independently.** The funnel is three
 * collection-group counts (~400 documents, ~760ms); the account list is 397
 * documents plus a batched 35-creator join (~571ms, measured for itself rather
 * than inherited, because only this one does the join). They are separate calls
 * so one failing degrades one card, and because the funnel legitimately sees
 * creators this screen cannot: it counts automations across ALL creators, while
 * the table is scoped to creators who have connected an account. The residual
 * gap between the two fire totals is real and is left visible rather than
 * reconciled away.
 *
 * The account's `tokenHash` and `tokenExpiration` are Instagram credentials.
 * The backend projects them away rather than filtering them out downstream, so
 * they never reach this screen at all.
 */

const STATUS_TONE: Record<string, string> = {
  Connected: viz.good,
  Paused: viz.warning,
};

const InstagramScreen = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetcher = useCallback(() => fetchInstagramAccounts(), []);
  const { data, error, loading } = useDashboardData<InstagramAccountsResponse>(
    fetcher,
    []
  );

  const query = searchParams.get("q") ?? "";
  const setQuery = useCallback(
    (value: string) => {
      const next = new URLSearchParams(searchParams);
      if (value) next.set("q", value);
      else next.delete("q");
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // The funnel itself, moved here from the Home tab in Sep 2026 when Home
  // stopped carrying any Instagram content.
  //
  // A second live call rather than something derived from the account list
  // above, because the two genuinely measure different populations: this counts
  // creators with automations across EVERY creator, while the account list only
  // sees creators who have connected an Instagram account. Deriving the funnel
  // from the list would silently drop the creators behind the 6-fire gap the
  // note at the bottom of this screen already explains.
  const funnelFetcher = useCallback(() => fetchInstagramFunnel(), []);
  const funnel = useDashboardData<InstagramFunnelResponse>(funnelFetcher, []);

  // Creators, not objects. Accounts / automations / fires are three different
  // things that grow at each stage rather than narrowing, so charting them as
  // funnel steps produced conversions of 465% and 2042%. A funnel has to follow
  // one population through the stages — which is what the Mixpanel funnel this
  // replaced was measuring too.
  const funnelSteps = funnel.data
    ? [
        {
          label: "Creators with an IG account",
          value: funnel.data.creators.withAccounts,
        },
        {
          label: "…who built an automation",
          value: funnel.data.creators.withAutomations,
        },
        {
          label: "…whose automation has fired",
          value: funnel.data.creators.withTriggeredAutomations,
        },
      ]
    : [];

  // The timelines behind the three funnel steps. Fetched separately from the
  // account list because the two sit on different clocks — the list is live,
  // this is the nightly rollup — and because a failure in one must not blank
  // the other. Same pattern ChannelSection uses for its Twilio call.
  const historyFetcher = useCallback(() => fetchInstagramHistory(), []);
  const history = useDashboardData<InstagramHistoryResponse>(historyFetcher, []);
  const historyFreshness = {
    kind: "cached" as const,
    updatedAt: history.data?.computedAt ?? undefined,
  };

  const accounts = useMemo(() => data?.accounts ?? [], [data]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return accounts;
    return accounts.filter(row =>
      `${row.creatorName} ${row.creatorUsername} ${row.instagramUsername} ${row.instagramAccountId}`
        .toLowerCase()
        .includes(needle)
    );
  }, [accounts, query]);

  const freshness = { kind: "live" as const };

  // Creators who connected an account but never built a live automation — the
  // drop-off the funnel's second step reports as a number and this screen can
  // name.
  //
  // Taken from the server's DISTINCT-creator totals rather than counted off the
  // rows here. Two creators have two Instagram accounts each, so counting rows
  // would count them twice — the same double-count that inflated platform fires
  // from 3,536 to 3,668 before it was caught.
  const withoutAutomations = data?.totals.creatorsWithoutLiveAutomation ?? 0;
  const neverFired = data?.totals.creatorsNeverFired ?? 0;

  const columns: Column<InstagramAccountRow>[] = [
    {
      key: "creator",
      label: "Creator",
      sortValue: row => (row.creatorName || row.creatorUsername).toLowerCase(),
      render: row => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Avatar
            url={row.profilePicUrl}
            fullName={row.creatorName}
            username={row.creatorUsername}
            size={28}
          />
          <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
            {row.creatorUsername ? (
              <a
                href={`https://markitai.com/u/${row.creatorUsername}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ ...type.bodyMedium, color: colors.ink, textDecoration: "none" }}
              >
                {row.creatorName || `@${row.creatorUsername}`}
              </a>
            ) : (
              <span style={{ ...type.bodyMedium, color: colors.inkFaint }} title={row.creatorUid}>
                (account not found)
              </span>
            )}
            <span style={{ ...type.bodySmallRegular, color: colors.inkFaint }}>
              @{row.creatorUsername || row.creatorUid}
            </span>
          </div>
        </div>
      ),
    },
    {
      key: "handle",
      label: "Instagram",
      sortValue: row => row.instagramUsername.toLowerCase(),
      render: row => (
        <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
          {row.instagramUsername ? (
            <a
              href={`https://instagram.com/${row.instagramUsername}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ ...type.bodyRegular, color: colors.ink, textDecoration: "none" }}
            >
              @{row.instagramUsername}
            </a>
          ) : (
            <span style={{ color: colors.inkFaint }}>—</span>
          )}
          <span
            style={{ ...type.bodySmallRegular, color: colors.inkFaint }}
            title={row.instagramAccountId}
          >
            {row.instagramAccountId}
          </span>
        </div>
      ),
    },
    {
      key: "status",
      label: "Status",
      sortValue: row => row.status,
      render: row => (
        <span
          style={{
            ...type.bodySmallMedium,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            color: colors.ink,
          }}
        >
          <span
            style={{
              width: 7,
              height: 7,
              borderRadius: 999,
              background: STATUS_TONE[row.status] ?? colors.inkFaint,
              flexShrink: 0,
            }}
          />
          {row.status || "—"}
        </span>
      ),
    },
    {
      key: "connected",
      label: "Connected",
      sortValue: row => row.connectedOn || "",
      render: row => (
        <span title={formatDate(row.connectedOn)}>{formatRelative(row.connectedOn)}</span>
      ),
    },
    {
      key: "automations",
      label: "Automations",
      numeric: true,
      sortValue: row => row.automationsLive,
      render: row => (
        <span
          style={{ color: row.automationsLive > 0 ? colors.ink : colors.inkFaint }}
          title={[
            row.automationsTotal === row.automationsLive
              ? undefined
              : `${row.automationsTotal - row.automationsLive} draft(s) excluded`,
            row.creatorAccountCount > 1
              ? `Creator-level figure — this creator has ${row.creatorAccountCount} connected accounts, so it repeats on each of their rows.`
              : undefined,
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {formatNumber(row.automationsLive)}
          {row.automationsTotal !== row.automationsLive && (
            <span style={{ color: colors.inkFaint }}> / {formatNumber(row.automationsTotal)}</span>
          )}
        </span>
      ),
    },
    {
      key: "fires",
      label: "Fires",
      numeric: true,
      sortValue: row => row.automationFires,
      render: row => (
        <span
          style={{
            color: row.automationFires > 0 ? colors.ink : colors.inkFaint,
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            justifyContent: "flex-end",
          }}
          title={
            row.creatorAccountCount > 1
              ? `Across ${formatNumber(
                  row.triggerDocuments
                )} trigger(s). Creator-level figure — this creator has ${
                  row.creatorAccountCount
                } connected accounts, so it repeats on each of their rows and must not be summed.`
              : `Across ${formatNumber(row.triggerDocuments)} trigger(s)`
          }
        >
          {/* A creator with two connected handles shows the same automation
              figures on both rows, because automations belong to the creator
              rather than to one handle. Marked so nobody adds them up. */}
          {row.creatorAccountCount > 1 && (
            <Icon
              icon="mdi:link-variant"
              height={12}
              style={{ color: colors.inkFaint }}
            />
          )}
          {formatNumber(row.automationFires)}
        </span>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Instagram"
        subtitle="The whole Instagram picture: how far the population gets, how each step has moved over time, and every creator behind it."
        right={
          <button
            onClick={() => navigate(dashboardPath.instagramDms())}
            style={{
              ...type.bodyMedium,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              color: colors.ink,
              background: colors.surface,
              border: `1px solid ${colors.border}`,
              borderRadius: 8,
              padding: "8px 14px",
              cursor: "pointer",
            }}
          >
            <Icon icon="ion:chatbubbles-outline" height={16} />
            DM Threads
          </button>
        }
      />

      {error && <ErrorBanner message={error} />}

      {/*
        The activation funnel, which lived on the Home tab until Sep 2026.

        It is here rather than deleted because it is the one thing the tiles
        below cannot say. Three separate counts answer "how many of each"; a
        funnel answers "where does the population drop out", which is a
        different question and the only one that identifies WHICH step is the
        problem. The tiles that sat beside it on Home did not come with it —
        every figure they carried already exists on this screen.
      */}
      <div style={{ marginBottom: 16 }}>
        <CardGrid min={380}>
          <ChartCard
            title="Instagram automations funnel"
            subtitle="Live · distinct creators at each stage, from Firestore"
            freshness={{ kind: "live" }}
            table={{
              categoryLabel: "Step",
              rows: funnelSteps.map(step => ({
                category: step.label,
                values: { value: step.value },
              })),
            }}
            series={[{ key: "value", label: "Creators", color: vizRamp[0] }]}
          >
            {funnel.error ? (
              <EmptyChartNote label={funnel.error} />
            ) : funnelSteps.length === 0 ? (
              <EmptyChartNote label="Loading…" />
            ) : (
              <FunnelBars steps={funnelSteps} />
            )}
          </ChartCard>
          <NoteCard title="Why the funnel counts creators rather than accounts, automations and fires">
            Those three totals are counts of different things — accounts,
            automations, and individual fires — and they <em>grow</em> at each
            stage rather than narrowing, so charting them as funnel steps produced
            step-to-step conversions of 465% and 2042%. A funnel has to follow one
            population through the stages, so every bar above is a count of{" "}
            <strong>distinct creators</strong>, which is also what the Mixpanel
            funnel this replaced was measuring. The raw totals live in the tiles
            below.
            {funnel.data && (
              <>
                {" "}
                Platform-wide right now:{" "}
                <strong>{formatNumber(funnel.data.accountsConnected)}</strong>{" "}
                accounts connected,{" "}
                <strong>{formatNumber(funnel.data.automationsCreated)}</strong>{" "}
                automations created (drafts excluded), and{" "}
                <strong>{formatNumber(funnel.data.automationsTriggered)}</strong>{" "}
                total fires across{" "}
                {formatNumber(funnel.data.triggerDocuments)} triggers.
              </>
            )}
          </NoteCard>
        </CardGrid>
      </div>

      {data && (
        <div style={{ marginBottom: 16 }}>
          <CardGrid min={200}>
            <StatTile
              label="Accounts connected"
              value={formatNumber(data.totals.accounts)}
              secondary={`Across ${formatNumber(data.totals.creators)} creators`}
              freshness={freshness}
            />
            <StatTile
              label="Automations"
              value={formatNumber(data.totals.automations)}
              secondary="Including drafts"
              freshness={freshness}
            />
            <StatTile
              label="No live automation"
              value={formatNumber(withoutAutomations)}
              secondary="Creators connected, but nothing switched on"
              tone={withoutAutomations > 0 ? "warning" : "neutral"}
              freshness={freshness}
            />
            <StatTile
              label="Never fired"
              value={formatNumber(neverFired)}
              secondary={`${formatNumber(
                data.totals.fires
              )} fires across connected creators`}
              freshness={freshness}
            />
          </CardGrid>
        </div>
      )}

      {/*
        The funnel's three steps, over time.

        Until Sep 2026 this screen and the funnel above it were both entirely
        point-in-time — three collection-group counts with nothing day-keyed
        stored anywhere — so "is Instagram activation growing" had no answer at
        all. Two of the three steps turned out to need no new tracking
        whatsoever: `instagramAccounts` carries `connectedOn` and `dmAutomations`
        carries `createdAt`, both populated on 100% of documents, so those
        timelines were reconstructed from data that was already there and reach
        back to the feature's own launch.

        The third could not be, and the charts say so rather than looking alike.
        See the note under them.
      */}
      <div style={{ marginBottom: 16 }}>
        <CardGrid min={380}>
          <CountChartCard
            title="Accounts connected"
            subtitle="Each account's own connectedOn — full history"
            seriesLabel="Accounts connected"
            days={history.data?.accountsConnectedByDay ?? []}
            color={viz.series1}
            freshness={historyFreshness}
            error={history.error}
            loading={history.loading}
            emptyLabel="The nightly sync hasn't built the Instagram history yet."
          />
          <CountChartCard
            title="Automations created"
            subtitle="Each automation's own createdAt — full history, drafts excluded"
            seriesLabel="Automations created"
            days={history.data?.automationsCreatedByDay ?? []}
            color={viz.series3}
            freshness={historyFreshness}
            error={history.error}
            loading={history.loading}
            emptyLabel="The nightly sync hasn't built the Instagram history yet."
            footnote={
              <>
                Drafts are excluded to match the funnel&rsquo;s &ldquo;automations
                created&rdquo; tile, so the two cannot report different definitions of
                the same word. One wrinkle that follows from it: an automation is
                excluded by its state <em>today</em>, not its state on the day it was
                made — so one created in June and moved to Draft in September drops out
                of June&rsquo;s bar retroactively. Firestore keeps no state-change
                history to do better.
              </>
            }
          />
        </CardGrid>
      </div>

      <div style={{ marginBottom: 16 }}>
        <CardGrid min={380}>
          <CountChartCard
            title="Triggers created"
            subtitle="Each trigger's own createdAt — full history"
            seriesLabel="Triggers created"
            days={history.data?.triggersCreatedByDay ?? []}
            color={viz.series7}
            freshness={historyFreshness}
            error={history.error}
            loading={history.loading}
            emptyLabel="The nightly sync hasn't built the Instagram history yet."
            footnote={
              <>
                Triggers <strong>created</strong>, not fired. The trigger documents carry
                a <code>createdAt</code>, so this one is real history — the{" "}
                <em>firing</em> of them is the series beside it, and that one is not.
              </>
            }
          />
          <CountChartCard
            title="Automations triggered"
            subtitle="Nightly snapshots of the fire counter, differenced"
            seriesLabel="Fires"
            days={history.data?.firesByDay ?? []}
            color={viz.series2}
            freshness={historyFreshness}
            error={history.error}
            loading={history.loading}
            defaultGranularity="day"
            emptyLabel={
              history.data && history.data.fireSnapshotDays < 2
                ? "Tracking has just started — the first bar appears after a second nightly run."
                : "The nightly sync hasn't started snapshotting fires yet."
            }
            startsFrom={
              <>
                This series starts{" "}
                <strong>
                  {history.data?.firesTrackedFrom
                    ? formatUtcDayKey(history.data.firesTrackedFrom)
                    : "when the nightly sync first ran"}
                </strong>
                , and nothing earlier can ever be added to it. Unlike the three charts
                above, there is <strong>no per-fire timestamp in Firestore</strong>:
                <code> numTriggered</code> is a single lifetime counter incremented on
                each fire, with no fire log and no dated record anywhere. So this chart
                is built by snapshotting that counter every night and subtracting
                consecutive days — which only works forward, from the day snapshotting
                began.{" "}
                {history.data?.totals && (
                  <>
                    The lifetime total is{" "}
                    <strong>{formatNumber(history.data.totals.fires)}</strong> fires; the
                    bars here sum only to what has been observed since tracking started.
                  </>
                )}
                {(history.data?.gapDays.length ?? 0) > 0 && (
                  <>
                    {" "}
                    <strong>
                      {formatNumber(history.data!.gapDays.length)} day
                      {history.data!.gapDays.length === 1 ? "" : "s"}
                    </strong>{" "}
                    {history.data!.gapDays.length === 1 ? "has" : "have"} no snapshot and{" "}
                    {history.data!.gapDays.length === 1 ? "is" : "are"} drawn as a gap
                    rather than folded into the next bar, which would invent a spike.
                  </>
                )}
                {(history.data?.counterDecreaseDays.length ?? 0) > 0 && (
                  <>
                    {" "}
                    On {formatNumber(history.data!.counterDecreaseDays.length)} day
                    {history.data!.counterDecreaseDays.length === 1 ? "" : "s"} the
                    counter went <em>down</em> — a trigger was deleted, taking its
                    lifetime fires with it. Those are gaps too, not negative days.
                  </>
                )}
              </>
            }
          />
        </CardGrid>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
          marginBottom: 16,
        }}
      >
        <input
          value={query}
          onChange={event => setQuery(event.target.value)}
          placeholder="Search creator or Instagram handle"
          style={{
            ...type.bodyRegular,
            color: colors.ink,
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: 8,
            padding: "8px 12px",
            minWidth: 280,
            outline: "none",
          }}
        />
        <span style={{ ...type.bodySmallRegular, color: colors.inkFaint }}>
          {formatNumber(filtered.length)} of {formatNumber(accounts.length)}
        </span>
        {data && (
          <TagChip
            label={`Fetched ${formatRelative(data.fetchedAt)}`}
            icon="ion:flash-outline"
            title="Read live from Firestore on this page load."
          />
        )}
      </div>

      {loading && <LoadingNote />}

      {data && (
        <>
          <DataTable
            rows={filtered}
            columns={columns}
            rowKey={row => `${row.creatorUid}-${row.instagramAccountId}`}
            initialSort={{ key: "connected", direction: "desc" }}
            emptyLabel={
              accounts.length === 0
                ? "No creator has connected an Instagram account."
                : "No accounts match this search."
            }
          />

          <div style={{ marginTop: 16 }}>
            <NoteCard title="This is the detail behind the funnel at the top of this screen">
              The funnel above charts how far the population gets — connected, then
              automated, then fired. This lists the creators behind those steps.{" "}
              <strong>Automations</strong> excludes drafts, matching the funnel; where
              a creator has drafts the total is shown after the slash.{" "}
              <strong>Fires</strong> is the sum of <code>numTriggered</code> across
              that creator&rsquo;s triggers, so one trigger that fired 500 times counts
              as 500 — a trigger with no <code>numTriggered</code> field yet counts as
              zero rather than being dropped, which keeps these totals reconcilable
              against the funnel&rsquo;s.
              {data.totals.accounts !== data.totals.creators && (
                <>
                  {" "}
                  <strong>
                    {formatNumber(data.totals.accounts - data.totals.creators)} of these
                    rows share a creator with another row.
                  </strong>{" "}
                  Automations and fires live under the creator, not under the
                  Instagram handle, so those figures repeat on each of that
                  creator&rsquo;s rows (marked with a link icon) and must not be added
                  together. Every total above is counted over distinct creators for
                  exactly that reason.
                </>
              )}
            </NoteCard>
          </div>
        </>
      )}
    </div>
  );
};

export default InstagramScreen;
